/**
 * EMI amortisation — the client mirror of the server's `installment.schedule.ts`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE MUST AGREE WITH THE SERVER, ROW FOR ROW AND PAISA FOR PAISA.
 *
 * The plan modal shows the operator all 24 dates and amounts BEFORE they commit,
 * and they read that table as a promise. If the server then generates anything
 * different, the preview was a lie — and it is the kind of lie nobody notices
 * until a customer queries their statement. So the arithmetic here is a faithful
 * port, not a re-derivation:
 *
 *   • integer paise everywhere, one rounding at the very end
 *   • remainder on the LAST row by default
 *   • due dates from a fixed anchor day with a month-end clamp, never from the
 *     previous row (that drifts to the 28th forever)
 *
 * The one DELIBERATE divergence: the server throws 400s, this returns
 * `{ errors }`. A live preview that recomputes on every keystroke must not throw
 * while someone is halfway through typing "24".
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type InstallmentFrequency =
  | "WEEKLY"
  | "FORTNIGHTLY"
  | "MONTHLY"
  | "QUARTERLY"
  | "HALF_YEARLY"
  | "YEARLY"
  | "CUSTOM";

export type InstallmentRounding = "LAST" | "FIRST";

/** Rupees -> paise. The only way money enters this module's arithmetic. */
export const toPaise = (rupees: number): number => Math.round(rupees * 100);

/** Paise -> rupees, for display and for the wire. */
export const fromPaise = (paise: number): number => paise / 100;

/** Months advanced per step, for the calendar-based cadences. */
const MONTH_STEP: Partial<Record<InstallmentFrequency, number>> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  HALF_YEARLY: 6,
  YEARLY: 12,
};

/** Days advanced per step, for the day-based cadences. */
const DAY_STEP: Partial<Record<InstallmentFrequency, number>> = {
  WEEKLY: 7,
  FORTNIGHTLY: 14,
};

export const MAX_INSTALLMENT_COUNT = 240;

export interface PreviewRow {
  seq: number;
  /** Rupees, 2dp. Σ over the schedule === principal, exactly. */
  amount: number;
  /** Local calendar date, "YYYY-MM-DD". Never a datetime — see `dueDateKey`. */
  dueDate: string;
}

export interface SchedulePreview {
  rows: PreviewRow[];
  /** Σ rows.amount, in rupees. Rendered next to the principal as the proof. */
  total: number;
  /** True when `total` and `principal` agree to the paisa. */
  balances: boolean;
  anchorDay: number;
  /** Blocking problems — the Create button stays disabled while non-empty. */
  errors: string[];
  /** Non-blocking things the operator should still see. */
  warnings: string[];
}

export interface SchedulePreviewInput {
  principal: number;
  frequency: InstallmentFrequency;
  intervalDays?: number | null;
  /** "YYYY-MM-DD" — a calendar day, not an instant. */
  startDate: string;
  rounding?: InstallmentRounding;
  /** Supply exactly one of these. */
  count?: number | null;
  amountPerInstallment?: number | null;
}

/* ── Calendar helpers ───────────────────────────────────────────────────────
 *
 * All of these work on "YYYY-MM-DD" STRINGS and integer parts, never on a Date
 * built from an ISO string. `new Date("2026-02-05")` parses as UTC midnight, so
 * in IST it renders as 05:30 on the 5th — and `.toISOString().slice(0,10)` on a
 * locally-constructed date can hand back the 4th. Both bugs shift a customer's
 * due date by a day, silently, depending on where the browser is. Staying in
 * integer y/m/d avoids the entire class.
 */

/** Days in a month. `m0` is 0-based. Leap-year aware. */
export function daysInMonth(y: number, m0: number): number {
  return new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
}

/** Parse "YYYY-MM-DD" into integer parts. Returns null if malformed. */
export function parseDateKey(
  key: string,
): { y: number; m0: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key ?? "");
  if (!m) return null;
  const y = Number(m[1]);
  const m0 = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (m0 < 0 || m0 > 11 || day < 1 || day > daysInMonth(y, m0)) return null;
  return { y, m0, day };
}

/** Integer parts back to "YYYY-MM-DD". */
export function dateKey(y: number, m0: number, day: number): string {
  const mm = String(m0 + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

/**
 * Step `n` months from an anchor, clamping to the target month's last day.
 *
 * Anchored on the 31st the correct ladder is
 *   31 Jan -> 28 Feb -> 31 Mar -> 30 Apr
 * whereas `setMonth(m + 1)` overflows February into 3 March (skipping the month
 * entirely) and stepping from the PREVIOUS row settles on the 28th forever.
 */
export function addMonthsClamped(
  anchor: { y: number; m0: number; anchorDay: number },
  n: number,
): { y: number; m0: number; day: number } {
  const total = anchor.m0 + n;
  const y = anchor.y + Math.floor(total / 12);
  const m0 = ((total % 12) + 12) % 12;
  return { y, m0, day: Math.min(anchor.anchorDay, daysInMonth(y, m0)) };
}

/** Step `n` days from a date key, via UTC so no local-timezone shift applies. */
export function addDaysKey(key: string, n: number): string {
  const p = parseDateKey(key);
  if (!p) return key;
  const d = new Date(Date.UTC(p.y, p.m0, p.day) + n * 86_400_000);
  return dateKey(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** "YYYY-MM-DD" for today in the browser's local calendar. */
export function todayKey(now: Date = new Date()): string {
  return dateKey(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Whole days between two date keys (b − a). Negative when b precedes a. */
export function daysBetween(a: string, b: string): number {
  const pa = parseDateKey(a);
  const pb = parseDateKey(b);
  if (!pa || !pb) return 0;
  return Math.round(
    (Date.UTC(pb.y, pb.m0, pb.day) - Date.UTC(pa.y, pa.m0, pa.day)) / 86_400_000,
  );
}

/**
 * What an instalment still owes, rounded to real paise.
 *
 * NEVER write `amount - paidAmount - waivedAmount` inline. Those are Floats:
 * 16666.66 − 10000.02 evaluates to 6666.639999999999, which then lands in an
 * input box as twelve decimal places and — worse — compares as GREATER than the
 * true remaining balance, so the payment it was prefilled for gets refused by
 * the deal's pending cap. The user sees "Pay" simply not working.
 */
export function outstandingOf(inst: {
  amount: number;
  paidAmount: number;
  waivedAmount?: number;
}): number {
  return fromPaise(
    Math.max(
      0,
      toPaise(inst.amount) -
        toPaise(inst.paidAmount) -
        toPaise(inst.waivedAmount ?? 0),
    ),
  );
}

/* ── The builder ───────────────────────────────────────────────────────────── */

/**
 * Build the amortisation table shown live in the plan modal.
 *
 * Never throws: invalid input comes back as `errors`, and the caller disables
 * the Create button while any are present.
 */
export function buildSchedulePreview(
  input: SchedulePreviewInput,
): SchedulePreview {
  const {
    principal,
    frequency,
    intervalDays,
    startDate,
    rounding = "LAST",
    count,
    amountPerInstallment,
  } = input;

  const errors: string[] = [];
  const warnings: string[] = [];
  const empty = (anchorDay = 1): SchedulePreview => ({
    rows: [],
    total: 0,
    balances: false,
    anchorDay,
    errors,
    warnings,
  });

  const start = parseDateKey(startDate);
  if (!start) {
    errors.push("Pick a valid start date.");
    return empty();
  }
  if (!Number.isFinite(principal) || principal <= 0) {
    errors.push("Enter an amount to finance.");
    return empty(start.day);
  }

  const hasCount = count != null && Number.isFinite(count) && count > 0;
  const hasAmount =
    amountPerInstallment != null &&
    Number.isFinite(amountPerInstallment) &&
    amountPerInstallment > 0;
  if (!hasCount && !hasAmount) {
    errors.push("Enter the number of instalments, or the amount for each.");
    return empty(start.day);
  }
  if (frequency === "CUSTOM" && (!intervalDays || intervalDays < 1)) {
    errors.push("A custom frequency needs an interval in days.");
    return empty(start.day);
  }

  const principalPaise = toPaise(principal);
  let n: number;
  let amountsPaise: number[];

  if (hasCount) {
    n = Math.floor(count as number);
    if (n > MAX_INSTALLMENT_COUNT) {
      errors.push(`No more than ${MAX_INSTALLMENT_COUNT} instalments.`);
      return empty(start.day);
    }
    if (n > principalPaise) {
      errors.push(
        `₹${principal.toFixed(2)} cannot be split ${n} ways — each instalment would be under a paisa.`,
      );
      return empty(start.day);
    }
    const base = Math.floor(principalPaise / n);
    const remainder = principalPaise - base * n;
    amountsPaise = new Array<number>(n).fill(base);
    if (remainder > 0) {
      if (rounding === "FIRST") {
        for (let i = 0; i < remainder; i += 1) {
          amountsPaise[i] = (amountsPaise[i] ?? base) + 1;
        }
        warnings.push(
          `Doesn't divide evenly — the first ${remainder} instalment${remainder > 1 ? "s carry" : " carries"} one extra paisa.`,
        );
      } else {
        amountsPaise[n - 1] = (amountsPaise[n - 1] ?? base) + remainder;
        warnings.push(
          `Doesn't divide evenly — the final instalment carries the remaining ${remainder} paisa.`,
        );
      }
    }
  } else {
    const perPaise = toPaise(amountPerInstallment as number);
    n = Math.ceil(principalPaise / perPaise);
    if (n > MAX_INSTALLMENT_COUNT) {
      errors.push(
        `₹${(amountPerInstallment as number).toFixed(2)} each would need ${n} instalments (max ${MAX_INSTALLMENT_COUNT}).`,
      );
      return empty(start.day);
    }
    amountsPaise = new Array<number>(n).fill(perPaise);
    const lastPaise = principalPaise - perPaise * (n - 1);
    amountsPaise[n - 1] = lastPaise;
    if (lastPaise !== perPaise) {
      warnings.push(
        `The final instalment is ₹${fromPaise(lastPaise).toFixed(2)} — the balance after ${n - 1} × ₹${fromPaise(perPaise).toFixed(2)}.`,
      );
    }
  }

  // ── Dates ────────────────────────────────────────────────────────────────
  const anchorDay = start.day;
  const monthStep = MONTH_STEP[frequency];
  const dayStep =
    frequency === "CUSTOM" ? (intervalDays as number) : DAY_STEP[frequency];

  const rows: PreviewRow[] = amountsPaise.map((paise, i) => {
    let due: string;
    if (monthStep) {
      const d = addMonthsClamped({ y: start.y, m0: start.m0, anchorDay }, i * monthStep);
      due = dateKey(d.y, d.m0, d.day);
    } else {
      due = addDaysKey(startDate, i * (dayStep ?? 0));
    }
    return { seq: i + 1, amount: fromPaise(paise), dueDate: due };
  });

  if (monthStep && anchorDay > 28) {
    warnings.push(
      `Anchored on day ${anchorDay} — shorter months fall due on their last day instead.`,
    );
  }

  const today = todayKey();
  const overdueCount = rows.filter((r) => r.dueDate < today).length;
  if (overdueCount > 0) {
    warnings.push(
      `${overdueCount} instalment${overdueCount > 1 ? "s" : ""} will be created already past due.`,
    );
  }

  const totalPaise = amountsPaise.reduce((acc, p) => acc + p, 0);
  return {
    rows,
    total: fromPaise(totalPaise),
    // Compared in paise, never as floats — this is the proof line the modal
    // renders, so it has to be exact rather than approximately right.
    balances: totalPaise === principalPaise,
    anchorDay,
    errors,
    warnings,
  };
}

/* ── Allocation preview (the spill breakdown) ───────────────────────────────── */

export interface AllocationSlice {
  installmentId: number;
  seq: number;
  amount: number;
}

export interface AllocationPreview {
  slices: AllocationSlice[];
  /** Anything left over once every unpaid instalment is covered. */
  unallocated: number;
}

/**
 * Split a payment across instalments, starting at the one the user picked and
 * spilling forward into later UNPAID rows.
 *
 * Only ever called to SHOW the operator what will happen — they picked the
 * anchor row explicitly, and this makes the consequence of typing a bigger
 * number visible instead of silently truncating it or hiding a waterfall.
 */
export function previewAllocation(
  amount: number,
  anchorInstallmentId: number,
  unpaid: Array<{ id: number; seq: number; outstanding: number }>,
): AllocationPreview {
  const ordered = [...unpaid].sort((a, b) => a.seq - b.seq);
  const startIdx = ordered.findIndex((i) => i.id === anchorInstallmentId);
  const chain = startIdx >= 0 ? ordered.slice(startIdx) : ordered;

  let leftPaise = toPaise(amount);
  const slices: AllocationSlice[] = [];
  for (const inst of chain) {
    if (leftPaise <= 0) break;
    const takePaise = Math.min(leftPaise, toPaise(inst.outstanding));
    if (takePaise <= 0) continue;
    slices.push({ installmentId: inst.id, seq: inst.seq, amount: fromPaise(takePaise) });
    leftPaise -= takePaise;
  }
  return { slices, unallocated: fromPaise(Math.max(0, leftPaise)) };
}
